/** Kuratiertes Tag-Vokabular.
 *
 *  Warum fest und nicht frei: Open Library liefert für ein einziges Buch schnell
 *  40 Schlagworte ("Fiction", "Ficción", "New York Times bestseller", "Large type
 *  books"). Bei 300 Büchern stehen dann 2.000 Tags im Regal und keiner taugt als
 *  Filter. Die KI darf deshalb nur ZUORDNEN, nicht erfinden. Freie Tags sind
 *  weiterhin möglich (kind = 'frei'), aber getrennt gehalten.
 *
 *  Die Liste ist bewusst auf die beiden Bibliotheken zugeschnitten (Sarah:
 *  Literatur, Fantasy, japanische Gegenwart · Mario: Sachbuch, Business, Hard-SciFi).
 */

import type { TagKind } from "./types";

export const GENRES = [
  "Literarischer Roman",
  "Gegenwartsliteratur",
  "Klassiker",
  "Fantasy",
  "High Fantasy",
  "Romantasy",
  "Science-Fiction",
  "Hard Science-Fiction",
  "Dystopie",
  "Krimi",
  "Thriller",
  "Historischer Roman",
  "Liebesroman",
  "Coming-of-Age",
  "Horror",
  "Kurzgeschichten",
  "Lyrik",
  "Graphic Novel",
  "Jugendbuch",
  "Sachbuch",
  "Biografie",
  "Wirtschaft",
  "Psychologie",
  "Philosophie",
  "Naturkunde",
  "Reise",
  "Essay",
  "Ratgeber",
] as const;

export const THEMEN = [
  "Familie",
  "Freundschaft",
  "Erste Liebe",
  "Ehe",
  "Trauer",
  "Erwachsenwerden",
  "Alter",
  "Krankheit",
  "Einsamkeit",
  "Identität",
  "Heimat",
  "Migration",
  "Krieg",
  "Nachkriegszeit",
  "Politik",
  "Macht",
  "Klasse und Geld",
  "Gerechtigkeit",
  "Schuld",
  "Rache",
  "Verbrechen",
  "Natur",
  "Meer",
  "Tiere",
  "Klima",
  "Reisen",
  "Kunst",
  "Musik",
  "Schreiben",
  "Wissenschaft",
  "Technik",
  "Künstliche Intelligenz",
  "Raumfahrt",
  "Zeit",
  "Religion",
  "Magie",
  "Mythologie",
  "Arbeit",
  "Unternehmertum",
  "Führung",
  "Produktivität",
  "Geld und Investieren",
  "Ernährung",
  "Sport",
  "Japan",
  "Skandinavien",
  "Großstadt",
  "Land und Dorf",
] as const;

export const STIMMUNGEN = [
  "leise",
  "melancholisch",
  "warm",
  "tröstlich",
  "humorvoll",
  "bissig",
  "spannend",
  "atemlos",
  "düster",
  "verstörend",
  "romantisch",
  "episch",
  "verspielt",
  "nüchtern",
  "klug",
  "hoffnungsvoll",
  "hart",
  "poetisch",
] as const;

export const EPOCHEN = [
  "Antike",
  "Mittelalter",
  "19. Jahrhundert",
  "Frühes 20. Jahrhundert",
  "Zwischenkriegszeit",
  "Zweiter Weltkrieg",
  "Nachkriegszeit",
  "Gegenwart",
  "Nahe Zukunft",
  "Ferne Zukunft",
  "Zeitlos",
] as const;

export const VOCABULARY: Record<Exclude<TagKind, "frei">, readonly string[]> = {
  genre: GENRES,
  thema: THEMEN,
  stimmung: STIMMUNGEN,
  epoche: EPOCHEN,
};

export const KIND_LABEL: Record<TagKind, string> = {
  genre: "Genre",
  thema: "Thema",
  stimmung: "Stimmung",
  epoche: "Epoche",
  frei: "Eigene",
};

/** Prüft, ob ein Tag zum kuratierten Vokabular gehört. Alles andere wird 'frei'. */
export function classifyTag(name: string): TagKind {
  const n = name.trim().toLowerCase();
  for (const kind of ["genre", "thema", "stimmung", "epoche"] as const) {
    if (VOCABULARY[kind].some((v) => v.toLowerCase() === n)) return kind;
  }
  return "frei";
}

/** Kanonische Schreibweise aus dem Vokabular, sonst der getrimmte Eingabewert. */
export function canonicalTag(name: string): string {
  const n = name.trim().toLowerCase();
  for (const kind of ["genre", "thema", "stimmung", "epoche"] as const) {
    const hit = VOCABULARY[kind].find((v) => v.toLowerCase() === n);
    if (hit) return hit;
  }
  return name.trim();
}

/**
 * Rohe Schlagworte einer Fremdquelle (Open Library "subjects") auf das Vokabular
 * abbilden. Bewusst konservativ: Was nicht sicher passt, fällt weg — lieber
 * ein Buch ohne Tags als ein Regal voller Rauschen.
 */
const SUBJECT_MAP: [RegExp, string[]][] = [
  [/science fiction|sciencefiction|sf/i, ["Science-Fiction"]],
  [/hard science fiction/i, ["Hard Science-Fiction"]],
  [/space|astronaut|mars|interstellar/i, ["Raumfahrt"]],
  [/dystopi/i, ["Dystopie"]],
  [/fantasy/i, ["Fantasy"]],
  [/epic fantasy|high fantasy/i, ["High Fantasy"]],
  [/magic|zauber/i, ["Magie"]],
  [/mytholog/i, ["Mythologie"]],
  [/detective|mystery|krimi/i, ["Krimi"]],
  [/thriller|suspense/i, ["Thriller"]],
  [/horror/i, ["Horror"]],
  [/romance|liebesroman/i, ["Liebesroman"]],
  [/coming of age|bildungsroman/i, ["Coming-of-Age"]],
  [/young adult|jugendbuch/i, ["Jugendbuch"]],
  [/historical fiction|historischer roman/i, ["Historischer Roman"]],
  [/classic|klassiker/i, ["Klassiker"]],
  [/poetry|lyrik|gedicht/i, ["Lyrik"]],
  [/short stories|erzählungen|kurzgeschichten/i, ["Kurzgeschichten"]],
  [/comic|graphic novel|manga/i, ["Graphic Novel"]],
  [/biograph|memoir|autobiograph/i, ["Biografie"]],
  [/business|management|entrepreneur/i, ["Wirtschaft", "Unternehmertum"]],
  [/leadership|führung/i, ["Führung"]],
  [/productivity|habits|gewohnheit/i, ["Produktivität"]],
  [/invest|finance|money|geld/i, ["Geld und Investieren"]],
  [/psycholog/i, ["Psychologie"]],
  [/philosoph/i, ["Philosophie"]],
  [/nature|natur|ecolog|ökolog/i, ["Natur"]],
  [/climate|klima/i, ["Klima"]],
  [/animal|tier|bird|vogel/i, ["Tiere"]],
  [/ocean|sea|meer|küste/i, ["Meer"]],
  [/travel|reise/i, ["Reisen"]],
  [/war|krieg/i, ["Krieg"]],
  [/world war|weltkrieg/i, ["Zweiter Weltkrieg"]],
  [/japan|tokyo|tokio/i, ["Japan"]],
  [
    /artificial intelligence|robot|künstliche intelligenz/i,
    ["Künstliche Intelligenz"],
  ],
  [/family|familie/i, ["Familie"]],
  [/friendship|freundschaft/i, ["Freundschaft"]],
  [/grief|trauer|loss/i, ["Trauer"]],
  [/identity|identität/i, ["Identität"]],
  [/crime|verbrechen/i, ["Verbrechen"]],
  [/justice|gerecht/i, ["Gerechtigkeit"]],
  [/religion|glaube|faith/i, ["Religion"]],
  [/food|nutrition|ernährung|kochen/i, ["Ernährung"]],
  [/sport|running|laufen/i, ["Sport"]],
  [/science|wissenschaft|physik|biolog/i, ["Wissenschaft"]],
  [/technolog|technik|computer|software/i, ["Technik"]],
  [/history|geschichte/i, ["Sachbuch"]],
  [/fiction/i, ["Literarischer Roman"]], // nur als letzter Anker
];

export function mapSubjects(subjects: string[]): string[] {
  const hits = new Set<string>();
  for (const raw of subjects) {
    for (const [re, tags] of SUBJECT_MAP) {
      if (re.test(raw)) tags.forEach((t) => hits.add(t));
    }
  }
  // "Literarischer Roman" ist der schwächste Treffer — nur behalten, wenn sonst nichts kam
  if (hits.size > 1) hits.delete("Literarischer Roman");
  return [...hits].slice(0, 8);
}

/** Prompt-Bausteine für das KI-Tagging. Liegt hier, damit Vokabular und Prompt
 *  nie auseinanderlaufen. */
export function taggingVocabularyBlock(): string {
  return [
    `GENRE: ${GENRES.join(", ")}`,
    `THEMA: ${THEMEN.join(", ")}`,
    `STIMMUNG: ${STIMMUNGEN.join(", ")}`,
    `EPOCHE: ${EPOCHEN.join(", ")}`,
  ].join("\n");
}
